/* 
    landing page for Checkin main page
*/

import React, { useState, useEffect } from 'react';
import PropTypes, { number } from 'prop-types';
import { Form, Row, Col, Table, Button, Input } from 'antd';
import moment from 'moment';
import PageTitle from 'shared_components/page_title';
import SearchPager from 'shared_components/search_pager';
import DropDown from 'modules/main/settings/shared/dropdown';
import { fetchCheckInSpecimen, fetchActiveLabLocations, fetchCurrentLocation } from 'services/check_in/check_in';
import { LOGGEDIN_USER_DATA, CURRENT_LAB_LOCATION } from 'global_config/constant-global';
import { AlphaNumInput, RegexInput } from 'shared_components/pattern_input';
import './checkin_search_patient_form.css';

const { useForm } = Form;

function Checkin({ children }) {
	const [dataSource, setDataSource] = useState([]);
	const [form] = useForm();
	const [isFormTouched, setIsFormTouched] = useState(false);
	const [locations, setLocations] = useState([]);
	const [pageSize, setPageSize] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	// const currentLabLocation = JSON.parse(sessionStorage.getItem(CURRENT_LAB_LOCATION));
	const loggedinUser = JSON.parse(sessionStorage.getItem(LOGGEDIN_USER_DATA));
	useEffect(() => {
		const fetchData = async () => {
			const data = await fetchActiveLabLocations();
			const currentLabLocation = await fetchCurrentLocation(loggedinUser.userID);

			const {setFieldsValue} = form;

			// Check if location is saved in sessionStorage, otherwise use current location
			const savedLocation = sessionStorage.getItem('CHECKIN_SELECTED_LOCATION');
			const locationToSet = savedLocation || currentLabLocation.checkinLocation;

			setFieldsValue({'location': locationToSet});
			setLocations(data);
		};
		fetchData();
	}, []);

	// Numeric sorter for SAMPLE ID (descending by default)
	const getNumericSorter = (dataSource, accessor) => {
		if (dataSource.length > 0) {
			const columnSorter = (a, b) => {
				const aVal = parseInt(accessor(a), 10) || 0;
				const bVal = parseInt(accessor(b), 10) || 0;
				return bVal - aVal; // descending order
			};
			return columnSorter;
		}
		return false;
	};

	const columns = [
		{
			title: 'SAMPLE ID',
			width: 100,
			dataIndex: 'sampleSpecimenID',
			sorter: getNumericSorter(dataSource, (record) => record.sampleSpecimenID),
			defaultSortOrder: 'descend',
			fixed: 'left',
		},
		{
			title: 'SPECIMEN',
			width: 100,
			dataIndex: 'specimenName',
			fixed: 'left',
		},
		{
			title: 'PATIENT ID',
			width: 100,
			dataIndex: 'patientID',
			fixed: 'left',
		},
		{
			title: 'LAST NAME',
			width: 150,
			dataIndex: 'lastName',
		},
		{
			title: 'FIRST NAME',
			width: 150,
			dataIndex: 'givenName',
		},
		{
			title: 'MIDDLE NAME',
			width: 150,
			dataIndex: 'middleName',
		},
		{
			title: 'CHECKED-IN BY',
			width: 150,
			dataIndex: 'checkinBy',
		},
		{
			title: 'CHECKED-IN DATE',
			width: 150,
			dataIndex: 'checkingDateTime',
			render: (text) => {
				if (text) {
					return moment(text).format('MM-DD-YYYY HH:mm A');
				}
				return '';
			},
		},
		{
			title: 'LOCATION',
			width: 150,
			dataIndex: 'checkinLocation',
			fixed: 'right',
		},
	];

	const options = locations.map(item => {
		return {
			label: item.labLocationName,
			value: item.labLocationName,
		};
	});

	const onCheckIn = async () => {
		const { getFieldsValue } = form;
		const { sampleID, location } = getFieldsValue();

		const payload = {
			sampleSpecimenID: sampleID,
			checkinLocation: location,
			userID: loggedinUser.userID,
		};

		const dataSet = await fetchCheckInSpecimen(payload);

		if (dataSet) {
			const newDataSource = [
				...dataSource,
				{
					sampleSpecimenID: dataSet.sampleSpecimenID,
					specimenID: dataSet.specimenID,
					specimenName: dataSet.specimenName,
					patientID: dataSet.patientID,
					lastName: dataSet.lastName,
					givenName: dataSet.givenName,
					middleName: dataSet.middleName,
					checkinBy: dataSet.checkinBy,
					checkingDateTime: dataSet.checkingDateTime,
					checkinLocation: dataSet.checkinLocation,
				},
			];

			// Sort by CHECK-IN DATE in descending order (newest first)
			newDataSource.sort((a, b) => {
				const aDate = new Date(a.checkingDateTime).getTime();
				const bDate = new Date(b.checkingDateTime).getTime();
				return bDate - aDate;
			});

			setDataSource(newDataSource);
		}

		form.setFieldsValue({ sampleID: undefined });
	};

	const onChangeSampleID = () => {
		setIsFormTouched(true);
	};

	const onChangeLocation = (value) => {
		// Save selected location to sessionStorage
		sessionStorage.setItem('CHECKIN_SELECTED_LOCATION', value);
		setIsFormTouched(true);
	};

	const isSubmitDisabled = (() => {
		if (isFormTouched) {
			return false;
		} else {
			return true;
		}
	})();

	const pageTitle = 'CHECK-IN';
	return (
		<div>
			<PageTitle pageTitle={pageTitle} />
			<div className="space-y-md"></div>
			<Form
				onFinish={onCheckIn}
				form={form}
				className="checkin-search-patient-form"
				layout="vertical"
			>
				<div className="container">
					<Col className="search-sample-id">
						<Form.Item
							name={'sampleID'}
							label="SAMPLE ID"
							rules={[
								{
									required: true,
									message: 'This field is required',
								},
								{
									pattern: new RegExp(/^-?(0|[1-9][0-9]*)(\.[0-9]*)?$/),
									message: 'Invalid Sample ID. Must be numeric.',
								},
							]}
						>
							<AlphaNumInput
								onChange={onChangeSampleID}
								maxLength={15}
								// regex={/^-?(0|[1-9][0-9]*)(\.[0-9]*)?$/}
							/>
						</Form.Item>
					</Col>
					<Col style={{ textAlign: 'center', marginTop: 20, fontSize: 14 }}>AND</Col>
					<Col className="search-location">
						<Form.Item
							label="LOCATION"
							name={'location'}
							rules={[{ required: true, message: 'This field is required' }]}
							// initialValue={currentLabLocation}
						>
							<DropDown
								size="small"
								placeholder="Filter by Location"
								content={options}
								onChange={onChangeLocation}
							/>
						</Form.Item>
					</Col>
					<Col style={{ marginTop: 24 }}>
						<Button
							// onClick={onCheckIn}
							htmlType="submit"
							type="primary"
							shape="round"
							disabled={isSubmitDisabled}
						>
							CHECK-IN
						</Button>
					</Col>
				</div>
			</Form>
			<div className="space-y-md"></div>
			<SearchPager 
				pageSize={pageSize}
				pageTotal={dataSource.length}
				currentPage={currentPage}
				handleChangeSize={setPageSize}
				pagerTitle="CHECK-IN RESULTS"
			/>
			<div className="space-y-sm"></div>
			<div className="checkin-search-patient-table">
				<Table 
					dataSource={dataSource} 
					columns={columns} 
					scroll={{ x: 1500 }}
					pagination={{
						pageSize,
						current: currentPage,
						showSizeChanger: false,
						onChange: (page) => setCurrentPage(page)
					}}
				/>
			</div>
		</div>
	);
}

Checkin.propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export default Checkin;
